import { useEffect, useState } from 'react';

import { ApiRequestError } from '@/lib/apiClient';
import { institutesApi } from '../api/institutes.api';

/**
 * Replacing and removing an institute's logo, for every screen that shows one.
 *
 * Holds whether a logo is set locally rather than refetching the record: on the
 * customer list a refetch would put every card back to its skeleton just to
 * change one image.
 */
export function useInstituteLogo(
  uuid: string,
  initialHasLogo: boolean,
  onError: (message: string | null) => void,
) {
  const [hasLogo, setHasLogo] = useState(initialHasLogo);
  const [isUploading, setIsUploading] = useState(false);
  /** Bumped after a change so SecureImage refetches instead of showing the old file. */
  const [version, setVersion] = useState(0);

  // A refetch of the record elsewhere on the page is the newer truth.
  useEffect(() => setHasLogo(initialHasLogo), [initialHasLogo]);

  async function upload(file: File) {
    setIsUploading(true);
    onError(null);
    try {
      await institutesApi.uploadLogo(uuid, file);
      setHasLogo(true);
      setVersion((current) => current + 1);
    } catch (caught) {
      onError(caught instanceof ApiRequestError ? caught.message : 'That image could not be uploaded.');
    } finally {
      setIsUploading(false);
    }
  }

  async function remove() {
    onError(null);
    try {
      await institutesApi.removeLogo(uuid);
      setHasLogo(false);
      setVersion((current) => current + 1);
    } catch (caught) {
      onError(caught instanceof ApiRequestError ? caught.message : 'The logo could not be removed.');
    }
  }

  return { hasLogo, isUploading, version, upload, remove };
}
